import { Children, isValidElement, useMemo } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { CodeBlock } from "@/components/docs/CodeBlock";
import { MermaidDiagram } from "@/components/docs/MermaidDiagram";
import { useDocs } from "@/lib/DocsContext";

const textOf = (children) =>
  Children.toArray(children)
    .map((c) => (typeof c === "string" ? c : isValidElement(c) ? textOf(c.props.children) : ""))
    .join("");

const Pre = ({ node, children }) => {
  const child = Children.toArray(children)[0];
  const className = (isValidElement(child) && child.props.className) || "";
  const lang = /language-([\w-]+)/.exec(className)?.[1];
  const code = textOf(isValidElement(child) ? child.props.children : children);
  const id = `md-code-${node?.position?.start?.line ?? 0}`;
  if (lang === "mermaid") return <MermaidDiagram code={code} testId={`mermaid-diagram-${node?.position?.start?.line ?? 0}`} />;
  return <CodeBlock code={code} language={lang} testId={id} />;
};

const Table = ({ children }) => (
  <div className="doc-table-wrap my-6 overflow-x-auto rounded-lg border border-border" data-testid="md-table">
    <table>{children}</table>
  </div>
);

const Anchor = ({ href = "", children }) => {
  const external = /^https?:\/\//.test(href);
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return <a href={href}>{children}</a>;
};

export const MarkdownView = ({ text }) => {
  const { docs } = useDocs();
  const paths = useMemo(() => new Set(docs.map((d) => d.path)), [docs]);

  const InlineCode = ({ children }) => {
    const value = textOf(children);
    if (paths.has(value)) {
      return (
        <Link to={`/doc/${value}`} className="doc-code-link" data-testid={`inline-doc-link-${value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          <code>{value}</code>
        </Link>
      );
    }
    return <code>{children}</code>;
  };

  return (
    <div className="doc-prose" data-testid="markdown-view">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{ pre: Pre, code: InlineCode, table: Table, a: Anchor }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};
